import { ImagePickerAsset } from "expo-image-picker";
import { XOR } from "ts-essentials";
import { DocumentPickerAsset } from "expo-document-picker";
import { cropZoomSelectionAtom } from "../atoms/crop-zoom";
import { useSetAtom } from "jotai";
import { Platform } from "react-native";
import { sleep } from "../utils";

export type UnifiedImagePickerAsset = Pick<
    XOR<DocumentPickerAsset, ImagePickerAsset>,
    "mimeType" | "uri" | "name" | "fileName" | "width" | "height" | "base64"
>;

type useCropZoomImageType = () => (
    asset: UnifiedImagePickerAsset,
) => Promise<UnifiedImagePickerAsset | null>;

const renameNameExtension = (uri?: string | null) => {
    if (!uri) {
        return undefined;
    }
    if (uri.indexOf(".") === -1) {
        return uri;
    }
    const parts = uri.split(".");
    parts.pop();
    return parts.join(".") + ".png";
};

export const useCropZoomImage: useCropZoomImageType = () => {
    const setCropZoomSelection = useSetAtom(cropZoomSelectionAtom);
    return async (asset) => {
        if (Platform.OS === "ios") {
            // For some reason, on iOS, since Expo 52 and React Native 0.76's updates,
            // if we setCropZoomSelection() too quick after the camera / library picker,
            // the ImageCropZoomModal will not be rendered correctly and will result in
            // a sort of "transparent" modal, locking the whole interface, forcing
            // the user to restart the app
            await sleep(500);
        }
        return new Promise((resolve) => {
            setCropZoomSelection({
                originalUrl: asset.uri,
                originalSize:
                    asset.height && asset.width
                        ? {
                              width: asset.width,
                              height: asset.height,
                          }
                        : undefined,
                onResolve: (url: string | undefined) => {
                    if (url) {
                        // the URL is always a PNG
                        resolve({
                            ...asset,
                            uri: url,
                            mimeType: "image/png",
                            name: renameNameExtension(asset.name),
                            fileName: renameNameExtension(asset.fileName),
                        });
                    } else {
                        resolve(null);
                    }
                },
            });
        });
    };
};
